import { useEffect, useState } from "react";
import { Dashboard } from "../../components/Dashboard/Dashboard";
import { Row } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import DataTable from "react-data-table-component";
import { Circular } from "../../components/Circular/Circular";
import connectTopLine from "../../components/HOC/connectTopLine";
import Search from "../../components/Search/Search";
import Export from "../../components/Export/Export";
import { applyCurrencyFormatters } from "../../common/helper";
import "./DynamicVariables.scss";
import { toastApiError } from "../../utils/toastApiError";
import useAuth from "../../auth/useAuth";
import {CircularProgress} from "@mui/material";
import {reportApi} from "../../api/reportApi";
import {useDebounce} from "../../hooks/useDebounce";

const customStyles = {
  header: {
    style: {
      fontSize: "24px",
      fontWeight: "800",
    },
  },
  headCells: {
    style: {
      fontSize: "14px",
      textAlign: "center",
    },
  },
};

const perPageList = [10, 50, 100];

const DynamicVariables = ({ datesFilter, brandsFilter }) => {
  const auth = useAuth();
  const [monthlyReports, setMonthlyReport] = useState([]);
  const [reports, setReport] = useState([]);
  const [secondReports, setSecondReport] = useState([]);
  const [stats, setStats] = useState({});
  const [search, setSearch] = useState("");
  const [monthlySearch, setMonthlySearch] = useState("");
  const [secondSearch, setSecondSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    reportApi.getVariablesReport(datesFilter, brandsFilter, search)
        .then((response) => {
          setReport(response.reports);
          setStats(response.totalsReport);
        })
        .catch(toastApiError)
        .finally(() => setLoading(false))
  }, [search, datesFilter, brandsFilter]);

  useEffect(() => {
    setLoading(true);
    reportApi.getSecondVariablesReport(datesFilter, brandsFilter, secondSearch)
        .then((response) => setSecondReport(response.reports))
        .catch(toastApiError)
        .finally(() => setLoading(false))
  }, [secondSearch, datesFilter, brandsFilter]);

  useEffect(() => {
    setLoading(true);
    reportApi.getMonthlyVariablesReport(datesFilter, brandsFilter, monthlySearch)
        .then((response) => setMonthlyReport(response.reports))
        .catch(toastApiError)
        .finally(() => setLoading(false))
  }, [monthlySearch, datesFilter, brandsFilter]);

  const columns = [
    {
      name: "Dynamic variable",
      selector: (row) => row.variable,
      sortable: true,
      grow: 3,
      style: {
        minWidth: "240px",
        fontSize: "14px",
      },
    },
    {
      name: "Brand",
      selector: (row) => row.brandName,
      sortable: true,
      style: {
        minWidth: "120px",
        fontSize: "14px",
      },
    },
    {
      name: "Date",
      selector: (row) => row.date,
      sortable: true,
      style: {
        width: "140px",
        fontSize: "14px",
      },
    },
    {
      name: "Clicks",
      selector: (row) => row.uniqueClicks,
      sortable: true,
      right: true,
    },
    {
      name: "Registrations",
      selector: (row) => row.registrations,
      sortable: true,
      right: true,
    },
    {
      name: "FTDs",
      selector: (row) => row.ftds,
      sortable: true,
      right: true,
    },
    {
      name: "No of deposits",
      selector: (row) => row.numberOfDeposits,
      sortable: true,
      right: true,
      omit: !auth.user.authorities.includes('VIEW_DEPOSITS')
    },
    {
      name: "Deposits",
      selector: (row) => row.deposits,
      sortable: true,
      format: "currency",
      right: true,
      omit: !auth.user.authorities.includes('VIEW_DEPOSITS')
    },
    {
      name: "Net Cash",
      selector: (row) => row.netCash,
      sortable: true,
      format: "currency",
      right: true,
      omit: !auth.user.authorities.includes('VIEW_NET_CASH')
    },
    {
      name: "Rev Share Commission",
      selector: (row) => row.revShareCommission,
      sortable: true,
      format: "currency",
      right: true,
    },
    {
      name: "CPA Commission",
      selector: (row) => row.cpaCommission,
      sortable: true,
      format: "currency",
      right: true,
    },
  ];

  const searchQuery = useDebounce((query) => setSearch(query), 600);
  const secondSearchQuery = useDebounce((query) => setSecondSearch(query), 600);
  const monthlySearchQuery = useDebounce((query) => setMonthlySearch(query), 600);

  return (
      <Row>
        <Dashboard stats={stats}/>
        <div className="data__wrapper">
          <Search searchQuery={monthlySearchQuery}/>
          {monthlyReports
              ? <DataTable
                  title="Monthly Dynamic variables ('v' parameter)"
                  columns={applyCurrencyFormatters(columns)}
                  data={monthlyReports}
                  progressPending={loading}
                  customStyles={customStyles}
                  pagination
                  progressComponent={<Circular/>}
                  actions={<Export rows={monthlyReports}/>}
                  paginationRowsPerPageOptions={perPageList}
              />
              : <CircularProgress/>
          }
        </div>

        <div className="data__wrapper">
          <Search searchQuery={searchQuery}/>
          {reports
              ? <DataTable
                  title="Daily Dynamic variables ('v' parameter)"
                  columns={applyCurrencyFormatters(columns)}
                  data={reports}
                  progressPending={loading}
                  customStyles={customStyles}
                  pagination
                  progressComponent={<Circular/>}
                  actions={<Export rows={reports}/>}
                  paginationRowsPerPageOptions={perPageList}
              />
              : <CircularProgress/>
          }
        </div>

        <div className="data__wrapper">
          <Search searchQuery={secondSearchQuery}/>
          {secondReports
              ? <DataTable
                  title="Daily Second Dynamic variables ('v2' parameter)"
                  columns={applyCurrencyFormatters(columns)}
                  data={secondReports}
                  progressPending={loading}
                  customStyles={customStyles}
                  pagination
                  progressComponent={<Circular/>}
                  actions={<Export rows={secondReports}/>}
                  paginationRowsPerPageOptions={perPageList}
              />
              : <CircularProgress/>
          }
        </div>
      </Row>
  );
};

export default connectTopLine(true, true, DynamicVariables);
